import {
  AnalyticsActionShareEvent,
  AnalyticsEventParam,
} from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { Ref, useImperativeHandle, useState } from 'react';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton,
} from 'react-share';
import styled from 'styled-components';

import { Item } from '#/client';

import { focusShadow } from '../common';
import { Modal } from './Modal';

export interface SharingModalApi {
  open: (item?: Item) => void;
  close: () => void;
}
interface SharingModalProps {
  apiRef?: Ref<SharingModalApi>;
}

// Twitter logo in package has not (possibly will not) be updated to X logo, so we need to put our own.
// Unfortunate solution to react-share being out-dated and no future update in sight.
// https://github.com/nygardk/react-share/pull/504
const LocalTwitterXIcon = () => (
  <svg height="64" viewBox="0 0 64 64" width="64">
    <circle cx={32} cy={32} fill="#0B2972" r={31} />
    <path
      d="M 41.116 18.375 h 4.962 l -10.8405 12.39 l 12.753 16.86 H 38.005 l -7.821 -10.2255 L 21.235 47.625 H 16.27 l 11.595 -13.2525 L 15.631 18.375 H 25.87 l 7.0695 9.3465 z m -1.7415 26.28 h 2.7495 L 24.376 21.189 H 21.4255 z"
      fill="white"
    ></path>
  </svg>
);

// even if we are testing locally, we still have to share the public URL.
// Hide it behind the goal guard to avoid bundling the local url into prod bundle
export const getUrl = () =>
  process.env.goal === 'local'
    ? window.location.href.replace(
        'http://localhost:8080/',
        'https://dev.web.one.accedo.tv/'
      )
    : window.location.href;

const IconRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1rem;

  button:focus:not(.focus-visible) {
    outline: none;
  }

  button.focus-visible {
    ${focusShadow}
  }
`;

export const SharingModal = ({ apiRef }: SharingModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [item, setItem] = useState<Item>();

  const api = {
    open: (item?: Item) => {
      setItem(item);
      setIsOpen(true);
    },
    close: () => {
      // #ExternalAnalytics - Share
      // TODO#analytics: only temporary solution to send this here, should be done in a better way
      document.dispatchEvent(
        new AnalyticsActionShareEvent({
          [AnalyticsEventParam.ContentId]: item?.id,
          [AnalyticsEventParam.Genre]: item?.genres,
          [AnalyticsEventParam.Title]: item?.title,
        })
      );

      setIsOpen(false);
    },
  };

  // Unlike the regular Modal, this one uses an imperative handle.
  // That means the PageController don't have to manage the modal open state, so the Page won't rerender every time.
  useImperativeHandle(apiRef, () => api);

  const buttonProps = {
    url: getUrl(),
    onShareWindowClose: api.close,
  };

  const iconProps = {
    round: true,
  };

  return (
    <Modal
      closeOnEsc
      closeOnOverlayClick
      isOpen={isOpen}
      onClose={api.close}
      showCloseButton
    >
      <h2>{t('button.share')}</h2>
      <IconRow>
        <FacebookShareButton {...buttonProps}>
          <FacebookIcon {...iconProps} />
        </FacebookShareButton>
        <TwitterShareButton {...buttonProps}>
          <LocalTwitterXIcon />
        </TwitterShareButton>
        <EmailShareButton {...buttonProps} subject={item?.title}>
          <EmailIcon {...iconProps} />
        </EmailShareButton>
      </IconRow>
    </Modal>
  );
};
